import { Box, Link, Typography } from '@mui/material';
import { ReactNode, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { defineMessage, FormattedMessage } from 'react-intl';

const showMoreMessage = defineMessage({
  id: 'common.prose.showMore',
  defaultMessage: 'Mostrar más',
});

const showLessMessage = defineMessage({
  id: 'common.prose.showLess',
  defaultMessage: 'Mostrar menos',
});

interface ExpandableProseProps {
  /** Prose content to display (string or rich text). */
  children: ReactNode;
  /** Max visible lines when collapsed. Defaults to 3. */
  lines?: 2 | 3;
}

/**
 * Long-form prose truncated with a CSS line clamp and a text toggle.
 * Prefer this over character-based truncation for descriptions and notes.
 */
export function ExpandableProse({ children, lines = 3 }: ExpandableProseProps) {
  const [expanded, setExpanded] = useState(false);
  const [needsToggle, setNeedsToggle] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  const measureOverflow = useCallback(() => {
    const el = textRef.current;
    if (!el) return;
    // Compare scroll vs client height while clamped to decide if the toggle is needed.
    setNeedsToggle(el.scrollHeight > el.clientHeight + 1);
  }, []);

  useLayoutEffect(() => {
    if (expanded) return;
    measureOverflow();
  }, [children, lines, expanded, measureOverflow]);

  return (
    <Box>
      <Typography
        ref={textRef}
        variant="body2"
        color="text.primary"
        component="div"
        sx={{
          ...(!expanded && {
            display: '-webkit-box',
            WebkitLineClamp: lines,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }),
        }}
      >
        {children}
      </Typography>

      {(needsToggle || expanded) && (
        <Link
          component="button"
          type="button"
          variant="body2"
          underline="hover"
          onClick={() => setExpanded((prev) => !prev)}
          sx={{ mt: 0.5, cursor: 'pointer' }}
        >
          <FormattedMessage {...(expanded ? showLessMessage : showMoreMessage)} />
        </Link>
      )}
    </Box>
  );
}
